// src/services/contextService.js
import api from '../api/apiClient';

/**
 * Service for handling context operations
 * API-first approach without localStorage fallback for loading
 */
const contextService = {
  /**
   * Get context data for a specific analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Object>} Context data
   */
  getContext: async (analysisId) => {
    try {
      // Try to get from API
      const response = await api.get(`/analyses/${analysisId}/context`);

      if (response.success) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch context data');
    } catch (error) {
      console.error('Error fetching context data:', error);
      // No localStorage fallback - API is the source of truth
      throw error;
    }
  },

  /**
   * Save context data for a specific analysis
   * @param {string} analysisId - Analysis ID
   * @param {Object} contextData - Context data to save
   * @returns {Promise<Object>} Saved context data
   */
  saveContext: async (analysisId, contextData) => {
    // Format data for API (if needed)
    const formattedData = {
      ...contextData,
      analysisId
    };

    // API is the sole source of truth — no localStorage fallback
    const response = await api.post(`/analyses/${analysisId}/context`, { data: formattedData });

    return response;
  },

  /**
   * Convert raw context data to the format expected by components
   * @param {Object} rawData - Raw context data from API
   * @returns {Object} Formatted context data including business assets and RACI matrix
   */
  formatContextData: (rawData) => {
    console.log('🔄 formatContextData called with:', rawData);

    // Handle cases where rawData might be null or not in the expected structure
    if (!rawData) {
      console.warn('formatContextData received null or undefined rawData');
      return { organizationName: '', missions: [], contraintes: [], hypotheses: [], scope: '', analysisDate: '', participants: [], businessAssets: [], matrix: {} };
    }

    // Check if data is nested under a 'data' property (common API pattern)
    const data = rawData.data ? rawData.data : rawData;
    console.log('📊 Extracted data:', data);

    // Ensure participants and businessAssets are always arrays
    const participants = Array.isArray(data.participants) ? data.participants : [];
    const businessAssets = Array.isArray(data.businessAssets) ? data.businessAssets : [];

    console.log('👥 Participants to format:', participants);

        // Convert participants to new format and build matrix for per-workshop role structure
        const matrix = {};
        const formattedParticipants = participants.map(p => {
            console.log(`👤 Processing participant:`, p);

            // Handle new per-workshop format (workshopRoles) vs old global format (role)
            if (p.workshopRoles && typeof p.workshopRoles === 'object') {
                // New per-workshop format: { "Atelier 1": ["R"], "Atelier 2": ["A","C"] }
                console.log(`✅ Using per-workshop format:`, p.workshopRoles);
                const normalized = {};
                Object.keys(p.workshopRoles).forEach(wId => {
                    const val = p.workshopRoles[wId];
                    normalized[wId] = Array.isArray(val) ? val : (val && val !== '' && val !== '-' ? [val] : []);
                });
                matrix[p.id] = normalized;
            } else if (p.role) {
                // Old global format: ["R", "A"] — keep as array, Context.js will convert to per-workshop
                console.log(`🔄 Using old role format (will convert to per-workshop): ${JSON.stringify(p.role)}`);
                matrix[p.id] = Array.isArray(p.role) ? p.role : (p.role && p.role !== '' && p.role !== '-' ? [p.role] : []);
            } else if (p.workshopRoles) {
                // Legacy per-workshop format with single string values — upgrade to arrays
                const upgraded = {};
                Object.keys(p.workshopRoles).forEach(wId => {
                    const val = p.workshopRoles[wId];
                    upgraded[wId] = val && val !== '' && val !== '-' ? [val] : [];
                });
                console.log(`🔄 Upgrading legacy per-workshop format:`, upgraded);
                matrix[p.id] = upgraded;
            } else {
                console.log(`⚠️ No role found for participant ${p.name}`);
                matrix[p.id] = [];
            }

            const formatted = {
                id: p.id,
                name: p.name || '',
                position: p.position || '',
                customPosition: p.customPosition || '',
                contact: p.contact || '',
                workshopRoles: matrix[p.id] && typeof matrix[p.id] === 'object' && !Array.isArray(matrix[p.id]) ? matrix[p.id] : {}
            };

            console.log(`📝 Formatted participant:`, formatted);
            return formatted;
        });

    console.log('🎯 Final matrix:', matrix);
    console.log('👥 Final formatted participants:', formattedParticipants);

    const result = {
      // Add default empty strings/arrays for robustness
      organizationName: data.organizationName || '',
      missions: Array.isArray(data.missions) ? data.missions : (data.mission ? [data.mission] : []),
      contraintes: Array.isArray(data.contraintes) ? data.contraintes : [],
      hypotheses: Array.isArray(data.hypotheses) ? data.hypotheses : [],
      scope: data.scope || '',
      analysisDate: data.analysisDate || '',
      participants: formattedParticipants,
      // Ensure business assets are extracted and defaulted to an empty array
      businessAssets: businessAssets,
      // Add matrix for RACI roles
      matrix: matrix,
      // Add responsible persons list for control measures
      responsiblePersonsList: Array.isArray(data.responsiblePersonsList) ? data.responsiblePersonsList : []
    };

    console.log('🎉 formatContextData result:', result);
    return result;
  },

  /**
   * Get context history
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Array>} Context history
   */
  getContextHistory: async (analysisId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/components/context/history`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching context history:', error);
      throw error;
    }
  }
};

export default contextService;
